import { Dialog, DialogContent, Link, Typography } from '@material-ui/core';
import { Info } from '@material-ui/icons';
import { useFormikEnhanced } from '@superdispatch/forms';
import { Color, Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { FormikDrawerContent } from 'shared/form/FormikDrawer';
import { formatCurrency, formatPlural } from 'shared/helpers/IntlHelpers';
import { useDebouncedValue } from 'shared/helpers/ReactHelpers';
import { filterSearchParams, useQuery } from 'shared/helpers/RoutingHelpers';
import { trackSubscriptionEvent } from '../data/SubscriptionAnalytics';
import {
  useBillingAddress,
  useEstimateSeatsUpgrade,
  useSubscription,
  useSubscriptionAPI,
} from '../SubscriptionAPI';
import { PlanUpdateDrawerLoadingContent } from './PlanUpdateDrawerLoadingContent';
import { SeatBasedPricingBox } from './SeatBasedPricingBox';
import { MaskedCardNumber } from './MaskedCardNumber';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';
import { PlanUpdateDrawerActions } from './PlanUpdateDrawerActions';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from 'shared/hooks/useBoolean';
import { CheckIcon } from 'shared/icons/CheckIcon';

const PaymentMethodLink = styled(Link)`
  cursor: pointer;
`;

interface UpgradedSeatsSummary {
  addedSeats: number;
  total?: string;
}

interface PlanUpdateDrawerContentProps {
  title?: ReactNode;
  isPending?: boolean;
  onClose: () => void;
  setStep: (step: 'plan_update' | 'card_details') => void;
  onSubmitSuccess: () => void;
}

export function PlanUpdateDrawerContent({
  title,
  onClose,
  setStep,
  isPending,
  onSubmitSuccess,
}: PlanUpdateDrawerContentProps) {
  const navigate = useNavigate();
  const upgradedSeatsModal = useBoolean();
  // Snapshot taken on submit so refetched subscription data doesn't change the dialog
  const [upgradedSeatsSummary, setUpgradedSeatsSummary] =
    useState<UpgradedSeatsSummary>();
  const [{ utm_medium, driver_guid, back_url }] = useQuery();
  const isPaymentPastDueEnabled = useFlag('payment_past_due');
  const { isLoading: isBillingAddressLoading } = useBillingAddress();
  const {
    subscription: subscriptionDetails,
    isSubscriptionSettingsLoading,
    billableSeats,
    isDriverSeat,
  } = useSubscription();
  const { upgradeSeats, collectPaymentPastDue } = useSubscriptionAPI();
  const [errorMsg, setErrorMsg] = useState<string>();
  const queryClient = useQueryClient();

  const purchasedSeatsCount = Number(
    subscriptionDetails?.subscription?.plan?.quantity,
  );
  const billableSeatsCount = Number(billableSeats?.billable_seats);
  const initialSeatsCount =
    purchasedSeatsCount > billableSeatsCount
      ? billableSeatsCount
      : purchasedSeatsCount + 1;

  const formik = useFormikEnhanced({
    initialValues: { seats_count: initialSeatsCount },
    async onSubmit(values) {
      const pastDueGUIDs: string[] =
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        seatsUpgrade?.past_due_invoices?.map((invoice) => invoice.guid) || [];

      if (pastDueGUIDs.length && isPaymentPastDueEnabled) {
        await Promise.all(
          pastDueGUIDs.map((guid) => collectPaymentPastDue(guid)),
        );
      }
      const summary: UpgradedSeatsSummary = {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        addedSeats: values.seats_count - seatsCount,
        total: seatsUpgrade?.total,
      };
      await upgradeSeats(values.seats_count);
      return summary;
    },
    validate: ({ seats_count }) => {
      if (
        !seats_count ||
        seats_count < (billableSeats?.billable_seats || 1) + 1
      ) {
        return {
          seats_count:
            'Please enter a number greater than the current number of drivers',
        };
      }
      return {};
    },
    onSubmitSuccess(summary) {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      trackSubscriptionEvent({ name: 'Carrier Updated Seats' });
      setUpgradedSeatsSummary(summary);
      upgradedSeatsModal.setTrue();
    },
    onSubmitFailure: (error) => {
      setErrorMsg(error.message);
    },
  });

  const { values } = formik;
  const { data: seatsUpgrade } = useEstimateSeatsUpgrade(values.seats_count);
  const seatsCount = billableSeats?.billable_seats || 1;
  const hasCreditCard = !!subscriptionDetails?.customer?.payment_method?.card;
  const cardDetails = subscriptionDetails?.customer?.payment_method?.card;
  const isDisabledMinus = formik.values.seats_count <= seatsCount + 1;

  const isLoading = isSubscriptionSettingsLoading || isBillingAddressLoading;
  const debouncedSeatsCount = useDebouncedValue(values.seats_count, 500);
  const addedSeats = upgradedSeatsSummary?.addedSeats ?? 0;

  return (
    <>
      <FormikDrawerContent
        onClose={onClose}
        formik={formik}
        title={title || 'Purchase Seats'}
        actions={
          !isLoading && (
            <PlanUpdateDrawerActions
              seatsCount={debouncedSeatsCount}
              purchasedSeatsCount={purchasedSeatsCount}
              isSubmitting={formik.isSubmitting || !!isPending}
              errorMsg={errorMsg}
            />
          )
        }
      >
        {isLoading ? (
          <PlanUpdateDrawerLoadingContent />
        ) : (
          <Box maxWidth="430px">
            <Stack space="large">
              <Stack space="small">
                <Box
                  padding="xsmall"
                  borderRadius="small"
                  backgroundColor="Blue50"
                >
                  <Columns align="top" space="xsmall">
                    <Column width="content">
                      <Info fontSize="small" htmlColor={Color.Blue300} />
                    </Column>
                    <Column width="fluid">
                      <Stack space="xxsmall">
                        {isDriverSeat ? (
                          <Stack space="xxsmall">
                            <Typography variant="body1">
                              Current Carrier TMS Plan: {seatsCount}{' '}
                              {seatsCount > 1 ? 'Drivers' : 'Driver'}
                            </Typography>
                            <Typography>
                              Inviting a new driver will update your plan.{' '}
                              <br />
                              To avoid changing your plan, replace a driver by
                              deactivating any driver first and then inviting.
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack space="xxsmall">
                            <Typography variant="body1">
                              Seat Limit Reached
                            </Typography>
                            <Typography>
                              You have {seatsCount}{' '}
                              {seatsCount > 1 ? 'seats' : 'seat'} today. To
                              invite another user, you’ll need to purchase 1
                              more seat.
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Column>
                  </Columns>
                </Box>
                <SeatBasedPricingBox
                  isMinusDisabled={isDisabledMinus}
                  isReadOnly={true}
                />
              </Stack>

              <Stack space="xsmall">
                <Typography color="textSecondary" variant="h6">
                  PAYMENT METHOD
                </Typography>
                {hasCreditCard ? (
                  <Inline space="xsmall" verticalAlign="center">
                    <Typography color="textSecondary">
                      Visa ending in{' '}
                      <MaskedCardNumber>
                        {cardDetails?.masked_number}
                      </MaskedCardNumber>
                    </Typography>
                    <PaymentMethodLink
                      onClick={() => {
                        setStep('card_details');
                      }}
                      color="primary"
                      underline="none"
                    >
                      Change
                    </PaymentMethodLink>
                  </Inline>
                ) : (
                  <PaymentMethodLink
                    onClick={() => {
                      navigate({
                        pathname: '/settings/subscription',
                        search: filterSearchParams({
                          utm_medium,
                          driver_guid,
                          back_url,
                        }),
                      });
                    }}
                    color="primary"
                    underline="none"
                  >
                    Add Payment Method
                  </PaymentMethodLink>
                )}
              </Stack>
            </Stack>
          </Box>
        )}
      </FormikDrawerContent>
      <Dialog
        open={upgradedSeatsModal.isEnabled}
        onClose={() => {
          onSubmitSuccess();
          upgradedSeatsModal.setFalse();
        }}
      >
        <DialogContent>
          <Box padding="xsmall" paddingBottom="large" maxWidth="420px">
            <Stack space="large" align="center">
              <CheckIcon style={{ width: '88px', height: '88px' }} />
              <Stack space="xsmall" align="center">
                <Typography variant="h2">Seats Number Updated</Typography>
                <Typography align="center">
                  You’ve successfully added{' '}
                  <b>
                    {addedSeats} {formatPlural(addedSeats, 'seat', 'seats')}
                  </b>{' '}
                  to your plan. Your card has been charged{' '}
                  <b>{formatCurrency(upgradedSeatsSummary?.total)}</b> (tax
                  included).
                </Typography>
              </Stack>
              <Button
                onClick={() => {
                  onSubmitSuccess();
                  upgradedSeatsModal.setFalse();
                }}
              >
                Got It
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
